import { useEffect, useRef, useState } from 'react';

const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));

export default function CategoryControls({
  roles,
  roleLabels = {},
  categoryColors,
  sectionTargets = [],
  onSelectCategory,
  onToggleNewCategory,
  onChangeCategoryColor,
  categoryTextColors = {},
  categoryTextThemes = {},
  onChangeCategoryTextTheme,
  onRenameCategory,
  onDeleteCategory,
  onMarkCategoryAsDeck,
  onMoveOrCopyCategory,
  onReorderCategories,
  allowAddCategory = true,
  allowColorChange = true,
  allowSectionTransfer = true,
  isSkeletonCategory = () => false,
  isCategoryDuplicate = () => false,
}) {
  const [categoryMenu, setCategoryMenu] = useState(null);
  const [categoryDeckConfirm, setCategoryDeckConfirm] = useState(null);
  const [activeSectionTransferMode, setActiveSectionTransferMode] = useState(null);
  const [renameModal, setRenameModal] = useState(null);
  const [draggedRole, setDraggedRole] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const longPressTimerRef = useRef(null);

  useEffect(() => {
    const closeMenu = () => {
      setCategoryMenu(null);
      setActiveSectionTransferMode(null);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeMenu();
    };

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const getRoleLabel = (role) => roleLabels[role] || role;

  const clampMenuPosition = (x, y) => {
    const MENU_W = 240;
    const MENU_H = 360;
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + MENU_W + margin > vw) { left = Math.max(margin, x - MENU_W); }
    left = Math.max(margin, Math.min(left, vw - MENU_W - margin));
    if (top + MENU_H + margin > vh) { top = Math.max(margin, vh - MENU_H - margin); }
    top = Math.max(margin, top);
    return { x: left, y: top };
  };
  const openCategoryMenu = (event, role) => {
    event.preventDefault();
    event.stopPropagation();
    const pos = clampMenuPosition(event.clientX, event.clientY);
    setCategoryMenu({ role, x: pos.x, y: pos.y });
  };

  const startCategoryLongPress = (event, role) => {
    const x = event.clientX;
    const y = event.clientY;
    if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null;
      const pos = clampMenuPosition(x, y);
      setCategoryMenu({ role, x: pos.x, y: pos.y });
    }, 500);
  };

  const cancelCategoryLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleCategoryClick = (role) => {
    if (categoryMenu && categoryMenu.role === role) return;
    onSelectCategory(role);
  };





  const handleColorChange = (event) => {
    if (!categoryMenu || !onChangeCategoryColor) return;
    onChangeCategoryColor(categoryMenu.role, event.target.value);
  };

  const handleDelete = () => {
    if (!categoryMenu) return;

    onDeleteCategory(categoryMenu.role);
    setCategoryMenu(null);
    setActiveSectionTransferMode(null);
  };

  const handleMakeCategoryDeck = () => {
    if (!categoryMenu) return;
    setCategoryDeckConfirm(categoryMenu.role);
    setCategoryMenu(null);
    setActiveSectionTransferMode(null);
  };

  const confirmCategoryDeck = () => {
    if (categoryDeckConfirm && onMarkCategoryAsDeck) onMarkCategoryAsDeck(categoryDeckConfirm);
    setCategoryDeckConfirm(null);
  };

  const handleRename = () => {
    if (!categoryMenu) return;

    setRenameModal({ role: categoryMenu.role, draft: getRoleLabel(categoryMenu.role) });
    setCategoryMenu(null);
    setActiveSectionTransferMode(null);
  };

  const getSectionTargets = () => {
    if (!categoryMenu || !allowSectionTransfer) return [];
    if (isSkeletonCategory(categoryMenu.role)) return [];

    return sectionTargets.filter((target) => (
      target.category !== categoryMenu.role && !target.path
    ));
  };

  const applySectionAction = (target, mode) => {
    if (!categoryMenu) return;

    onMoveOrCopyCategory(categoryMenu.role, target, mode);
    setCategoryMenu(null);
    setActiveSectionTransferMode(null);
  };

  const getDropPlacement = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientX > rect.left + rect.width / 2 ? 'after' : 'before';
  };

  const startCategoryDrag = (event, role) => {
    setDraggedRole(role);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', role);
  };

  const dragOverCategory = (event, role) => {
    if (!draggedRole || draggedRole === role) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropTarget({ role, placement: getDropPlacement(event) });
  };

  const dropCategory = (event, role) => {
    event.preventDefault();
    const sourceRole = draggedRole || event.dataTransfer.getData('text/plain');
    const placement = getDropPlacement(event);

    if (sourceRole && sourceRole !== role) {
      onReorderCategories(sourceRole, role, placement);
    }

    setDraggedRole(null);
    setDropTarget(null);
  };

  const endCategoryDrag = () => {
    setDraggedRole(null);
    setDropTarget(null);
  };

  return (
    <nav className="category-controls" aria-label="Доступные роли">
      {roles.map((role) => (
        role !== 'END' && (
          <button
            className={[
              'role-button',
              draggedRole === role ? 'is-dragging' : '',
              dropTarget?.role === role && dropTarget.placement === 'before' ? 'drop-before' : '',
              dropTarget?.role === role && dropTarget.placement === 'after' ? 'drop-after' : '',
            ].filter(Boolean).join(' ')}
            key={role}
            type="button"
            draggable={!isTouchDevice}
            onClick={() => handleCategoryClick(role)}
            onContextMenu={(event) => openCategoryMenu(event, role)}
            onPointerDown={(event) => startCategoryLongPress(event, role)}
            onPointerUp={cancelCategoryLongPress}
            onPointerLeave={cancelCategoryLongPress}
            onPointerCancel={cancelCategoryLongPress}
            onDragStart={(event) => startCategoryDrag(event, role)}
            onDragOver={(event) => dragOverCategory(event, role)}
            onDrop={(event) => dropCategory(event, role)}
            onDragEnd={endCategoryDrag}
            style={{ backgroundColor: categoryColors[role] || '#64748b', color: categoryTextColors[role] || '#ffffff', touchAction: 'manipulation' }}
            title="Удерживайте для настроек"
          >
            + {getRoleLabel(role)}
          </button>
        )
      ))}

      {allowAddCategory && (
      <button
        className="add-category-button"
        type="button"
        onClick={onToggleNewCategory}
        aria-label="Добавить главную категорию"
      >
        +
      </button>
      )}

      {categoryMenu && (
        <div
          className="word-context-menu category-context-menu"
          style={{ left: categoryMenu.x, top: categoryMenu.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="word-context-close"
            onClick={() => setCategoryMenu(null)}
            aria-label="Закрыть"
          >
            ×
          </button>
          <div className="word-context-title">{getRoleLabel(categoryMenu.role)}</div>

          {allowColorChange && (
            <label className="category-color-action">
              <span>Цвет категории</span>
              <input
                type="color"
                value={categoryColors[categoryMenu.role] || '#64748b'}
                onChange={handleColorChange}
              />
            </label>
          )}
          {allowColorChange && onChangeCategoryTextTheme && (
            <div className="category-textcolor-action">
              <span>Цвет шрифта</span>
              <div className="category-textcolor-toggle">
                <button type="button" className={'cat-fc-btn' + ((categoryTextThemes[categoryMenu.role] === 'dark') ? ' is-active' : '')} onClick={() => onChangeCategoryTextTheme(categoryMenu.role, 'dark')}>Тёмный</button>
                <button type="button" className={'cat-fc-btn' + ((categoryTextThemes[categoryMenu.role] === 'light') ? ' is-active' : '')} onClick={() => onChangeCategoryTextTheme(categoryMenu.role, 'light')}>Светлый</button>
              </div>
            </div>
          )}

          <button
            className="word-context-action"
            type="button"
            onClick={handleRename}
          >
            Переименовать категорию
          </button>

          {onMarkCategoryAsDeck && (
            <button
              className="word-context-action"
              type="button"
              onClick={handleMakeCategoryDeck}
            >
              Сделать колодой
            </button>
          )}

          {getSectionTargets().length > 0 && (
            <>
              <button
                className="word-context-action transfer-toggle"
                type="button"
                onClick={() => setActiveSectionTransferMode((mode) => (mode === 'copy' ? null : 'copy'))}
              >
                <span>Дублировать раздел</span>
                <span className="context-arrow">›</span>
              </button>

              {activeSectionTransferMode === 'copy' && (
                <div className="word-context-inline-list">
                  {getSectionTargets().map((target) => (
                    <button
                      key={`${target.category}-${target.path}`}
                      type="button"
                      title={target.label}
                      onClick={() => applySectionAction(target, 'copy')}
                    >
                      {target.label}
                    </button>
                  ))}
                </div>
              )}

              <button
                className="word-context-action transfer-toggle"
                type="button"
                onClick={() => setActiveSectionTransferMode((mode) => (mode === 'move' ? null : 'move'))}
              >
                <span>Перенести раздел</span>
                <span className="context-arrow">›</span>
              </button>

              {activeSectionTransferMode === 'move' && (
                <div className="word-context-inline-list">
                  {getSectionTargets().map((target) => (
                    <button
                      key={`${target.category}-${target.path}`}
                      type="button"
                      title={target.label}
                      onClick={() => applySectionAction(target, 'move')}
                    >
                      {target.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <button
            className={'word-context-action ' + ((isCategoryDuplicate(categoryMenu.role) || !isSkeletonCategory(categoryMenu.role)) ? 'danger-action' : '')}
            type="button"
            onClick={handleDelete}
          >
            {isCategoryDuplicate(categoryMenu.role)
              ? 'Удалить дубль'
              : (isSkeletonCategory(categoryMenu.role) ? 'Выключить категорию' : 'Удалить категорию')}
          </button>
        </div>
      )}
      {categoryDeckConfirm && (
        <div className="deck-confirm-overlay" onClick={() => setCategoryDeckConfirm(null)}>
          <div className="deck-confirm-box" onClick={(e) => e.stopPropagation()}>
            <p className="deck-confirm-text">Сделать всю категорию «{getRoleLabel(categoryDeckConfirm)}» отдельной колодой? В неё попадут слова всех её подкатегорий.</p>
            <div className="deck-confirm-actions">
              <button type="button" className="deck-confirm-yes" onClick={confirmCategoryDeck}>Да</button>
              <button type="button" className="deck-confirm-no" onClick={() => setCategoryDeckConfirm(null)}>Нет</button>
            </div>
          </div>
        </div>
      )}
      {renameModal && (
        <div className="subcat-delete-overlay" onClick={() => setRenameModal(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Переименовать категорию</p>
            <form onSubmit={(e) => { e.preventDefault(); const t = (renameModal.draft || '').trim(); if (!t) return; onRenameCategory(renameModal.role, t); setRenameModal(null); }}>
              <input className="subcat-row-input" type="text" autoFocus value={renameModal.draft} onChange={(e) => setRenameModal((m) => m ? { ...m, draft: e.target.value } : m)} placeholder="Название категории" />
              <div className="subcat-delete-actions">
                <button type="button" className="subcat-delete-cancel" onClick={() => setRenameModal(null)}>Отмена</button>
                <button type="submit" className="subcat-row-confirm">Сохранить</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}
